
import React, { PropTypes } from "react";
import { MultiGrid, AutoSizer, CellMeasurerCache } from "react-virtualized";
import { categoryType, selectOptionType, vendorType } from "custom-prop-types";
import styles from "components/manage-categories/manage-categories.scss";
import { Button } from "components/common/button";
import { AutomateInlineTabs } from "components/common/automate-inline-tabs";
import { CellRenderer, getGridHeight } from "components/grid/grid-components";
import { NoItems } from "components/common/no-items";
import { AddVendorRulesModal } from "components/manage-vendors/add-vendor-rules-modal";
import { MergeVendorsModal } from "components/manage-vendors/merge-vendors-modal";

export const ManageVendors = ({
  accountId,
  categories,
  isAddVendorRulesLoading,
  isAddVendorRulesMode,
  isMergeVendorsLoading,
  isMergeVendorsMode,
  onAddVendorRules,
  onDeleteIncludeRule,
  onDeselectVendors,
  onEndAddVendorRulesMode,
  onEndMergeVendorsMode,
  onMergeVendors,
  onOrderBy,
  onSelectCategory,
  onSelectMergeVendor,
  onSelectVendors,
  onStartAddVendorRulesMode,
  onStartMergeVendorsMode,
  orderBy,
  orderDirection,
  selectedCategory,
  selectedVendorIds,
  selectedVendor,
  slug,
  vendors
}) => (
  <div className={ styles.container }>
    <AutomateInlineTabs currentPage="manage-vendors" slug={ slug } />
    <h1>Manage Vendors</h1>
    <div className={ styles["controls-container"] }>
      <Button
        disabled={ selectedVendorIds.length < 2 }
        onClick={ onStartMergeVendorsMode }
        primary
      >
        Merge Vendors
      </Button>
      <Button
        disabled={ !selectedVendorIds.length > 0 }
        onClick={ onStartAddVendorRulesMode }
        primary
      >
        Add vendor rules
      </Button>
      <AddVendorRulesModal
        categories={ categories }
        isAddVendorRulesLoading={ isAddVendorRulesLoading }
        isAddVendorRulesMode={ isAddVendorRulesMode }
        onAddVendorRules={ onAddVendorRules }
        onEndAddVendorRulesMode={ onEndAddVendorRulesMode }
        onSelectCategory={ onSelectCategory }
        selectedCategory={ selectedCategory }
        selectedVendorIds={ selectedVendorIds }
      />
      <MergeVendorsModal
        accountId={ accountId }
        isMergeVendorsLoading={ isMergeVendorsLoading }
        isMergeVendorsMode={ isMergeVendorsMode }
        onEndMergeVendorsMode={ onEndMergeVendorsMode }
        onMergeVendors={ onMergeVendors }
        onSelectMergeVendor={ onSelectMergeVendor }
        selectedVendor={ selectedVendor }
        selectedVendorIds={ selectedVendorIds }
      />
    </div>
    <div className={ styles["grid-container"] }>
      <AutoSizer disableHeight>
        {({ width }) => (
          <MultiGrid
            accountId={ accountId }
            cache={ cache }
            cellRenderer={ ({ ...gridProps }) => <CellRenderer { ...gridProps } newKey={ gridProps.key } /> }
            classNameBottomRightGrid={ styles["bottom-grid"] }
            classNameTopRightGrid={ styles["top-grid"] }
            columnCount={ columns.length }
            columns={ columns }
            columnWidth={ ({ index }) => getColumnWidth(index, width, columns.length) }
            deferredMeasurementCache={ cache }
            documents={ vendors }
            fixedRowCount={ 1 }
            height={ getGridHeight(-1, vendors.length, 45) }
            isManageVendorsPage
            isRowFocusable
            isSelectable
            noContentRenderer={ () => <NoItems name={ "vendors" } /> }
            onDeleteIncludeRule={ onDeleteIncludeRule }
            onFocusRow={ onSelectVendors }
            onOrderBy={ onOrderBy }
            onRowsDeselected={ onDeselectVendors }
            onRowsSelected={ onSelectVendors }
            orderBy={ orderBy }
            orderDirection={ orderDirection }
            rowCount={ vendors.length + 1 }
            rowHeight={ 45 }
            selectedDocumentIds={ selectedVendorIds }
            width={ width }
          />
        )}
      </AutoSizer>
    </div>
  </div>
);

ManageVendors.defaultProps = {
  selectedCategory: null,
  selectedVendorIds: [],
  selectedVendor: null
};

ManageVendors.propTypes = {
  accountId: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  isAddVendorRulesLoading: PropTypes.bool.isRequired,
  isAddVendorRulesMode: PropTypes.bool.isRequired,
  isMergeVendorsLoading: PropTypes.bool.isRequired,
  isMergeVendorsMode: PropTypes.bool.isRequired,
  onAddVendorRules: PropTypes.func.isRequired,
  onDeleteIncludeRule: PropTypes.func.isRequired,
  onDeselectVendors: PropTypes.func.isRequired,
  onEndAddVendorRulesMode: PropTypes.func.isRequired,
  onEndMergeVendorsMode: PropTypes.func.isRequired,
  onMergeVendors: PropTypes.func.isRequired,
  onOrderBy: PropTypes.func.isRequired,
  onSelectCategory: PropTypes.func.isRequired,
  onSelectMergeVendor: PropTypes.func.isRequired,
  onSelectVendors: PropTypes.func.isRequired,
  onStartAddVendorRulesMode: PropTypes.func.isRequired,
  onStartMergeVendorsMode: PropTypes.func.isRequired,
  orderBy: PropTypes.string.isRequired,
  orderDirection: PropTypes.string.isRequired,
  selectedCategory: categoryType,
  selectedVendor: selectOptionType,
  selectedVendorIds: PropTypes.arrayOf(PropTypes.string),
  slug: PropTypes.string.isRequired,
  vendors: PropTypes.arrayOf(vendorType).isRequired
};

const columns = [
  {
    key: "select",
    visible: true
  },
  {
    key: "name",
    name: "Vendors",
    sortable: true,
    visible: true
  },
  {
    key: "receipts",
    name: "Number of Receipts",
    sortable: true,
    visible: true
  },
  {
    key: "rules",
    name: "Vendor Rules",
    sortable: true,
    visible: true
  }
];

const getColumnWidth = (index, width, columnsLength) => {
  switch (index) {
    case 0:
      return 75;
    default:
      return (width - 75) / (columnsLength - 1);
  }
};

const cache = new CellMeasurerCache({
  defaultWidth: 300,
  minWidth: 250,
  fixedHeight: true
});




// WEBPACK FOOTER //
// ./src/js/components/manage-vendors/manage-vendors.jsx